import { MONTH_INDEX_BY_NAME, WEEK_KEYWORD } from './statusUpdateSignalKeywords.ts';

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const BOUNDARY_START = '(?<![\\p{L}\\p{N}])';
const BOUNDARY_END = '(?![\\p{L}\\p{N}])';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Year assumed for title dates that omit one ("17/06", "Aug 11", "W24").
 */
const ASSUMED_YEAR = 2000;

const isValidMonthDay = (month: number, day: number): boolean =>
	month >= 1 && month <= 12 && day >= 1 && day <= 31;

/** ISO date, e.g. "2026-08-12". */
const parseIso = (text: string): number | undefined => {
	const match = new RegExp(`${BOUNDARY_START}(\\d{4})-(\\d{2})-(\\d{2})${BOUNDARY_END}`, 'u').exec(
		text,
	);
	if (!match) {
		return undefined;
	}
	const month = Number(match[2]);
	const day = Number(match[3]);
	return isValidMonthDay(month, day) ? Date.UTC(Number(match[1]), month - 1, day) : undefined;
};

/** Numeric slash date, e.g. "17/06", "08/12", "12/08/2026". Interpreted as day/month. */
const parseSlash = (text: string): number | undefined => {
	const match = new RegExp(
		`${BOUNDARY_START}(\\d{1,2})/(\\d{1,2})(?:/(\\d{2,4}))?${BOUNDARY_END}`,
		'u',
	).exec(text);
	if (!match) {
		return undefined;
	}

	const first = Number(match[1]);
	const second = Number(match[2]);

	// Day/month by default; only flip when the numbers make month/day unambiguous.
	let day = first;
	let month = second;
	if (first <= 12 && second > 12) {
		month = first;
		day = second;
	}

	if (!isValidMonthDay(month, day)) {
		return undefined;
	}

	let year = ASSUMED_YEAR;
	if (match[3] !== undefined) {
		const parsed = Number(match[3]);
		year = parsed < 100 ? 2000 + parsed : parsed;
	}
	return Date.UTC(year, month - 1, day);
};

/**
 * Week number, e.g. "W24" or "Week 24". Consecutive week numbers resolve exactly
 * seven days apart, which is all cadence detection needs.
 */
const parseWeek = (text: string): number | undefined => {
	const weekWord = escapeRegExp(WEEK_KEYWORD.trim());
	const patterns = [`${BOUNDARY_START}W(\\d{1,2})${BOUNDARY_END}`];
	if (weekWord) {
		patterns.push(`${BOUNDARY_START}${weekWord}\\s+(\\d{1,2})${BOUNDARY_END}`);
	}

	for (const pattern of patterns) {
		const match = new RegExp(pattern, 'iu').exec(text);
		if (match) {
			const week = Number(match[1]);
			if (week >= 1 && week <= 53) {
				return Date.UTC(ASSUMED_YEAR, 0, 1) + (week - 1) * 7 * MS_PER_DAY;
			}
		}
	}
	return undefined;
};

/** Month name next to a day in either order, e.g. "Aug 11", "12 Aug", "Week of Aug 11". */
const parseMonthDay = (text: string): number | undefined => {
	const monthIndexByName = MONTH_INDEX_BY_NAME;
	const monthNamePatterns: string[] = [];
	for (const name of monthIndexByName.keys()) {
		const escaped = escapeRegExp(name);
		if (escaped.length > 0) {
			monthNamePatterns.push(escaped);
		}
	}
	const alternation = monthNamePatterns.join('|');
	if (!alternation) {
		return undefined;
	}

	const monthFirst = new RegExp(
		`${BOUNDARY_START}(${alternation})\\s+(\\d{1,2})${BOUNDARY_END}`,
		'iu',
	).exec(text);
	const dayFirst = monthFirst
		? null
		: new RegExp(`${BOUNDARY_START}(\\d{1,2})\\s+(${alternation})${BOUNDARY_END}`, 'iu').exec(text);

	const match = monthFirst ?? dayFirst;
	if (!match) {
		return undefined;
	}

	const monthName = (monthFirst ? match[1] : match[2]).toLowerCase();
	const day = Number(monthFirst ? match[2] : match[1]);
	const monthIndex = monthIndexByName.get(monthName);
	if (monthIndex === undefined || day < 1 || day > 31) {
		return undefined;
	}
	return Date.UTC(ASSUMED_YEAR, monthIndex, day);
};

/**
 * Extracts a date encoded in a title and returns it as a UTC timestamp (ms), or
 * `undefined` when the title carries no recognisable date. Supports ISO dates
 * ("2026-08-12"), numeric slash dates ("17/06", "08/12"), week numbers ("W24",
 * "Week 24") and a month name next to a day ("Aug 11", "12 Aug").
 *
 * Dates that omit a year assume a fixed year (2000)
 */
export const parseTitleDate = (text: string | null | undefined): number | undefined => {
	if (!text) {
		return undefined;
	}
	return parseIso(text) ?? parseSlash(text) ?? parseWeek(text) ?? parseMonthDay(text);
};
